import React, { useContext, useState } from 'react';
import {
  TicketClient,
  GET_FIELD,
  UPDATE_TICKET,
  GET_TECHNICIANS_BY_CRITERIA,
  AccountClient,
  UPDATE_TASK,
  DELETE_TASK,
  GET_ALL_TECH_GROUP
} from '@nile/graphql';

import { useLazyQuery, useQuery } from '@apollo/client';
import { convertMinutesToHours } from '@nile/mangifera/src/Utils/helpers';
import {
  toast,
  Loader,
  SearchDropdown,
  getTranslatedText,
  AppContext,
  TICKET_SOURCE,
  Tag,
  ALL_MODULES,
  DEFAULT_FIELDS_BY_COLUMN_NAME,
  UnknownTicketActions,
  NewTask,
  useProduct,
  getPriorityBadgeStatus
} from '@nile/components';
import { clone, cloneDeep, isEqual } from 'lodash';
import {
  Card,
  Avatar,
  Spinner,
  TextStyle,
  Badge
} from '@superops/ui-design-system';
import dompurify from 'dompurify';
import { Tooltip } from 'react-clone-tippy';
import { FormattedMessage, injectIntl } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import TicketInfoPopup from '@nile/mangifera/src/Components/Ticket/TicketInfoPopup';
import styled from 'styled-components';
import { constructTaskObject, renderTime } from '@nile/projects/src/Utils';
import dayjs from 'dayjs';
import {
  CellWrapper,
  DisabledTag
} from '@nile/mangifera/src/Pebbles/TaskViewContainer';
import { dompurifier } from '@nile/components/src/Utils/helpers';
import { getTechnicianVariables } from '@nile/graphql/src/Constants/variablesResolver';

const KanbanCard = styled.div`
  width: calc(100% - 20px);
  .Superops-Card {
    margin: 0 !important;
    margin-bottom: 16px !important;
    padding: 12px 0;
    background: ${(props) =>
      props.bgColor ? props.bgColor : 'var(--sops-white-to-grey-bg)'};
    box-sizing: border-box;
    border-radius: 8px;
    box-shadow: none !important;
  }

  &:hover .Superops-Card {
    // box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1) !important;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.05) !important;
  }
`;

const ReqClientDetails = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 21px;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 10px;
  span {
    margin: 0 5px;
  }
`;

const ReqClientName = styled.div`
  color: rgba(0, 0, 0, 0.85);
  max-width: ${(props) => props.maxWidth || '110px'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Subject = styled.div`
  display: inline-flex;
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  cursor: pointer;
`;

const TechnicianContainer = styled.div`
  cursor: pointer;
  position: relative;
  .removeTech {
    position: absolute;
    top: -6px;
    right: -6px;
    display: none;
    background: white;
    border-radius: 50%;
    svg {
      width: 12px;
      height: 12px;
    }
  }
  &:hover .assignTech svg {
    color: #323232;
  }
  &:hover .removeTech {
    display: flex;
    svg:hover {
      color: #ff7a7a;
    }
  }
  .removeTech svg:hover {
    color: #ff7a7a;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
`;

const IdContainer = styled(Header)``;

const StyledTag = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 21px;
  color: ${(props) => props.color || '#ffffff'};
  background: ${(props) => props.background};
  border-radius: 4px;
  ${(props) =>
    props.leftAligned &&
    `
    padding: 2px 10px 2px 5px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  `}
  ${(props) =>
    props.rightAligned &&
    `
    padding: 2px 5px 2px 10px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  `}
  max-width: ${(props) => props.maxWidth || '120px'};
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  &.status-cell {
    display: inline;
    vertical-align: middle;
  }
`;

const TechnicianListItem = styled.div`
  .Superops-Avatar__Svg {
    width: 25px;
    height: 25px;
  }
  &.selected .Superops-Avatar {
    border: 2px solid #4238fd;
  }
`;

function TaskKanbanCard(props) {
  const {
    context,
    item,
    activeTab,
    board,
    dispatch,
    list,
    intl,
    isNotDraggable: isEditRestricted,
    updateList,
    refetchList
  } = props;
  const flags = useFlags();
  const [sheetActive, setSheetActive] = useState(false);
  const currentProduct = useProduct();

  const toggleSheetActive = () => {
    setSheetActive(!sheetActive);
  };
  const {
    priority,
    displayId,
    requester,
    ticketId,
    status,
    technician,
    source,
    latestReply,
    workItem,
    title
  } = item;
  const { client, module, site, subject } = workItem || {};
  const appContext = useContext(AppContext);
  const currentUser = appContext?.user;
  const isStatus = activeTab === 'status';
  const isTechnician = activeTab === 'technician';
  const isTechGroupAvailable = item.techGroup;
  const isSourceVisible =
    source === TICKET_SOURCE.Schedule || source === TICKET_SOURCE.Ai;
  const [technicianLoading, setTechnicianLoading] = useState(false);
  const { loading, data: priorityValues, error } = useQuery(GET_FIELD, {
    client: AccountClient,
    variables: {
      input: {
        module: ALL_MODULES.ticket,
        columnName: DEFAULT_FIELDS_BY_COLUMN_NAME.PRIORITY
      }
    }
  });
  const [
    getTechniciansList,
    { loading: techLoading, data: technicians, error: techError }
  ] = useLazyQuery(GET_TECHNICIANS_BY_CRITERIA, {
    client: AccountClient
  });

  const [
    getTechnicianGroup,
    { loading: techGrpLoading, data: technicianGroups, error: techGroupError }
  ] = useLazyQuery(GET_ALL_TECH_GROUP, { client: AccountClient });

  const getSourceIcon = () => {
    for (const [key, value] of Object.entries(TICKET_SOURCE)) {
      if (value === source) {
        return key;
      }
      return 'inline-error';
    }
  };

  if (loading) return <Loader />;
  if (error) return <div>An error occurred...</div>;
  const colors = {};
  let currentTechnician = technician ? [technician] : [];
  const fldOptions =
    priorityValues?.getField?.options || priorityValues?.getField?.fieldOptions;
  fldOptions?.forEach((el) => {
    colors[el.value] = el?.details?.colorCode;
  });
  const openTicket = () => {
    context.openTab({
      module: 'TICKET',
      data: {
        subject: subject,
        displayId: displayId,
        ticketId: ticketId
      }
    });
  };

  const activeTabMap = {
    group: {
      accessor: 'techGroup',
      key: 'groupId'
    },
    technician: {
      accessor: 'technician',
      key: 'userId'
    },
    status: { accessor: 'status' }
  };

  const updateTask = (updatedTask, taskId, args, callback, errorCallback) => {
    const taskObj = constructTaskObject(updatedTask);
    TicketClient.mutate({
      mutation: UPDATE_TASK,
      variables: {
        taskId: taskId,
        module: args.ticket ? 'TICKET' : 'PROJECT',
        input: taskObj
      }
    })
      .then((res) => {
        if (res?.data?.updateTask) {
          const newUpdatedTask = res?.data?.updateTask;
          const newBoardData = cloneDeep(board);
          const targetLaneIdx = newBoardData.findIndex(
            (el) => el.id === list.id
          );
          const itemToUpdate = newBoardData[targetLaneIdx].items.findIndex(
            (el) => el.taskId === newUpdatedTask.taskId
          );
          newBoardData[targetLaneIdx].items[itemToUpdate] = {
            ...newBoardData[targetLaneIdx].items[itemToUpdate],
            ...newUpdatedTask
          };
          dispatch({ type: 'resetBoard', board: newBoardData });
          toast('success', 'Task updated successfully');
          updateList();
          const { accessor, key } = activeTabMap[activeTab];
          const identifier = key
            ? newUpdatedTask[accessor]?.[key]
            : newUpdatedTask[accessor];
          const updatedList = board.find((list) => {
            if (identifier) {
              return list.id === identifier;
            }
            return list.id === 'Unassigned tickets';
          });
          if (updatedList && refetchList) {
            refetchList(updatedList);
          }

          callback && callback();
        } else {
          errorCallback && errorCallback();
        }
      })
      .catch((err) => {
        // toast('error', 'Something went wrong');
        callback && callback();
      });
  };

  const deleteTask = (taskId, args, callback) => {
    TicketClient.mutate({
      mutation: DELETE_TASK,
      variables: {
        taskId: taskId,
        module: args.ticket ? 'TICKET' : 'PROJECT'
      }
    })
      .then((res) => {
        if (res?.data?.deleteTask) {
          const deletedTask = res?.data?.deleteTask;
          const newBoardData = cloneDeep(board);
          const targetLaneIdx = newBoardData.findIndex(
            (el) => el.id === list.id
          );
          const itemToUpdate = newBoardData[targetLaneIdx].items.findIndex(
            (el) => el.taskId === deletedTask
          );
          newBoardData[targetLaneIdx].count -= 1;
          newBoardData[targetLaneIdx].items.splice(itemToUpdate, 1);
          dispatch({ type: 'resetBoard', board: newBoardData });
          toast(
            'success',
            getTranslatedText(intl, 'ticket.details.task.delete.success')
          );
          updateList();
          callback && callback();
        }
      })
      .catch((err) => {
        // toast('error', 'Something went wrong');
        callback && callback();
      });
  };

  function updateTechnician(technicianArr = null) {
    setTechnicianLoading(true);
    const technicianDetails = technicianArr && {
      ...technicianArr[technicianArr.length - 1]
    };
    // save technician api call
    if (!isEqual(item.technician, technicianDetails)) {
      const updatedTask = cloneDeep(item);
      updatedTask.technician = technicianDetails;
      updateTask(
        updatedTask,
        item?.taskId,
        item,
        () => setTechnicianLoading(false),
        () => setTechnicianLoading(false)
      );
    }
  }

  function removeTechnician() {
    updateTechnician(null);
  }

  function highlightCurrentUserAndCurrentTechnician(techniciansList) {
    const values = [{ id: currentUser?.userId, index: 0 }];
    if (currentUser?.userId !== technician?.userId) {
      values.push({ id: technician?.userId, index: 1 });
    }
    const clonedTechniciansList = clone(techniciansList);
    if (clonedTechniciansList?.length) {
      values.forEach((val) => {
        clonedTechniciansList?.forEach((listItem, idx) => {
          if (listItem['userId'] === val.id) {
            clonedTechniciansList.splice(
              val.index,
              0,
              clonedTechniciansList.splice(idx, 1)[0]
            );
          }
        });
      });
    }
    return clonedTechniciansList;
  }

  const getDropdronListItems = () => {
    if (isTechGroupAvailable) {
      getTechnicianGroup();
    } else {
      getTechniciansList({
        variables: getTechnicianVariables({
          clientIds: [client?.accountId],
          siteIds: [site?.id],
          currentProduct,
          isListInfoRequired: true
        })
      });
    }
  };

  function getUserList() {
    let userList = [];
    if (isTechGroupAvailable) {
      const currentTechGroup = technicianGroups?.getTechnicianGroups?.find(
        (techGrp) => techGrp.groupId === item.techGroup.groupId
      );
      if (currentTechGroup) {
        userList = currentTechGroup.userList || [];
      }
    } else {
      userList = technicians?.getTechnicianList?.userList || [];
    }

    return highlightCurrentUserAndCurrentTechnician(userList);
  }

  function technicianListItem({ item }) {
    const clonedValues =
      currentTechnician && currentTechnician.length
        ? clone(currentTechnician)
        : [];

    const alreadyValueSelected = clonedValues.find(
      (value) => value?.userId === item?.userId
    );

    const isCurrentUser = item.userId === currentUser.userId;

    return (
      <TechnicianListItem
        className={`fx actr ${alreadyValueSelected ? 'selected' : ''}`}
      >
        <Avatar initials={item?.name?.[0]} name={item?.name} size="small" />
        <span className="ml-10">
          {isCurrentUser
            ? getTranslatedText(
                intl,
                'ticket.technicians.dropdown.isCurrentUser'
              )
            : item?.name}
        </span>
      </TechnicianListItem>
    );
  }

  const getSource = (data) => {
    if (workItem?.module === ALL_MODULES.project) {
      // return 'ProjectLogo';
      return 'ProjectFilled';
    }
    if (workItem?.module === ALL_MODULES.ticket) {
      return 'TicketFilled';
      // return 'ticket-outline';
    }
  };

  const updateTaskHandler = (ut, updatedTask) => {
    updateTask(updatedTask, item?.taskId, item, toggleSheetActive);
  };
  const onDeleteHandler = () => {
    deleteTask(item?.taskId, item, toggleSheetActive);
  };

  const backgroundColorMap = {
    Cancelled: '#E0E0E0',
    Blocked: '#F7C1C1'
  };

  return (
    <KanbanCard>
      <Card>
        <Header className="fx fx-sb">
          <IdContainer>
            <>
              {/* <StyledTag
                className="priority-cell"
                leftAligned
                maxWidth="140px"
                color="#424242"
                background={colors[priority] || '#9797974D'}
              >
                <span className="ml8">#{displayId}</span> */}
              {/* </StyledTag> */}
              <Badge status={getPriorityBadgeStatus(priority?.toLowerCase())}>
                {displayId}
              </Badge>
            </>
          </IdContainer>
          {!isStatus && (
            <StyledTag
              className="status-cell uppercase ellipsis"
              color="rgba(0, 0, 0, 0.85)"
              rightAligned
              maxWidth="140px"
              background={
                backgroundColorMap[status] || 'var(--sops-light-blue)'
              }
            >
              <Tooltip
                title={dompurifier(status)}
                delay={300}
                duration={250}
                position="bottom"
                inertia
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
              >
                {status}
              </Tooltip>
            </StyledTag>
          )}
        </Header>
        <div className="mt10 pl10 pr10">
          <ReqClientDetails className="fx actr">
            <div
              className="flex center g10"
              onClick={openTicket}
              style={{ lineBreak: 'anywhere' }}
            >
              <div className="flex self-start">
                <SVGIcon name={getSource()} />
              </div>
              <ReqClientName maxWidth="220px">{subject}</ReqClientName>
            </div>
          </ReqClientDetails>
          <Subject>
            <CellWrapper>
              <div>
                <div className="text-wrap" onClick={toggleSheetActive}>
                  <TextStyle>{item.title}</TextStyle>
                </div>
                <NewTask
                  addOrUpdateTechGroup={updateTaskHandler}
                  //   addOrUpdateTechGroup={() => {}}
                  state={sheetActive}
                  toggleSheetActive={toggleSheetActive}
                  name={title}
                  hideIcon
                  noRender
                  data={item}
                  module={
                    workItem?.module === 'PROJECT'
                      ? 'PROJECT_TASK'
                      : 'TICKET_TASK'
                  }
                  details={
                    workItem?.module === 'PROJECT'
                      ? {
                          operation: 'UPDATE',
                          taskId: item?.taskId,
                          projectId: workItem.workId
                        }
                      : {
                          operation: 'UPDATE',
                          taskId: item?.taskId,
                          ticketId: workItem.workId
                        }
                  }
                  showParent
                  onDeleteHandler={onDeleteHandler}
                  locked
                />
              </div>
            </CellWrapper>
          </Subject>
          {item.estimatedTime ? (
            <div className="flex center mt-10 g10">
              <SVGIcon name="EstimatedTime" />
              <div>{convertMinutesToHours(item.estimatedTime)}</div>
            </div>
          ) : (
            ''
          )}
          <div className="fx full-width actr jspbtwn mt-10">
            <div className="mlauto">
              {!isTechnician && (
                <Tooltip
                  title={
                    technician
                      ? technician?.['name'] ||
                        `${technician?.['firstName']} ${technician?.['lastName']}`
                      : getTranslatedText(intl, 'Assignee')
                  }
                  delay={400}
                  duration={250}
                  position="top"
                  inertia
                  trigger="mouseenter"
                  animation="shift"
                  animateFill={false}
                >
                  <TechnicianContainer
                    className={`${
                      isEditRestricted
                        ? 'cursor-not-allowed pointer-events-none'
                        : ''
                    }`}
                  >
                    {technicianLoading ? (
                      <Spinner size="small" />
                    ) : (
                      <>
                        <SearchDropdown
                          currentValues={currentTechnician}
                          updateValues={updateTechnician}
                          listItems={getUserList()}
                          RenderItem={technicianListItem}
                          hoverBg={'#e8efff'}
                          activator={
                            <span onClick={getDropdronListItems}>
                              {technician ? (
                                <Avatar
                                  initials={
                                    technician?.['name']?.[0] ||
                                    technician?.['firstName']?.[0]
                                  }
                                  name={
                                    technician?.['name'] ||
                                    technician?.['firstName']
                                  }
                                  size="extraSmall"
                                />
                              ) : (
                                <span className="assignTech">
                                  <SVGIcon
                                    name="Assign"
                                    size="md"
                                    color="#848484"
                                  />
                                </span>
                              )}
                            </span>
                          }
                          loading={techGrpLoading || techLoading}
                          error={techGroupError || techError}
                        />
                        {technician && (
                          <span
                            className="removeTech"
                            onClick={removeTechnician}
                          >
                            <SVGIcon
                              size="sm"
                              name="CircleCancelMinor"
                              color="#979797"
                            />
                          </span>
                        )}
                      </>
                    )}
                  </TechnicianContainer>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </Card>
    </KanbanCard>
  );
}

export default injectIntl(TaskKanbanCard);
